import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  VStack,
  HStack,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
} from "@chakra-ui/react";
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import { sizesService, categoriesService } from "api";

// Nested Category Selector Component
function NestedCategorySelector({ categories, selectedCategory, onCategorySelect, placeholder, zIndex = 1000, forForm = false }) {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Debug logging
  useEffect(() => {
    console.log('NestedCategorySelector - categories:', categories);
    console.log('NestedCategorySelector - selectedCategory:', selectedCategory);
    console.log('NestedCategorySelector - forForm:', forForm);
  }, [categories, selectedCategory, forForm]);
  
  // Filter out child categories to prevent duplication
  const getRootCategories = (cats) => {
    if (!Array.isArray(cats)) return [];
    
    const allChildIds = new Set();
    
    // Collect all child category IDs
    const collectChildIds = (categoryList) => {
      categoryList.forEach(cat => {
        const children = cat.children || cat.subcategories || cat.child_categories || [];
        if (Array.isArray(children) && children.length > 0) {
          children.forEach(child => {
            allChildIds.add(child.id);
            // Recursively collect nested children
            if (child.children || child.subcategories || child.child_categories) {
              collectChildIds([child]);
            }
          });
        }
      });
    };
    
    collectChildIds(cats);
    
    // Return only categories that are not children of other categories
    return cats.filter(cat => !allChildIds.has(cat.id));
  };

  // For forms, get only childless categories (categories without children)
  const getChildlessCategories = (cats) => {
    if (!Array.isArray(cats)) return [];
    
    const childless = [];
    
    const findChildless = (categoryList) => {
      categoryList.forEach(cat => {
        const children = cat.children || cat.subcategories || cat.child_categories || [];
        if (!Array.isArray(children) || children.length === 0) {
          // This category has no children, add it to childless list
          childless.push(cat);
        } else {
          // This category has children, recursively check its children
          findChildless(children);
        }
      });
    };
    
    findChildless(cats);
    return childless;
  };
  
  const renderCategoryItem = (category, level = 0) => {
    // Check if category has children in different possible formats
    const hasChildren = (category.children && Array.isArray(category.children) && category.children.length > 0) ||
                       (category.subcategories && Array.isArray(category.subcategories) && category.subcategories.length > 0) ||
                       (category.child_categories && Array.isArray(category.child_categories) && category.child_categories.length > 0);
    
    const children = category.children || category.subcategories || category.child_categories || [];
    const isSelected = selectedCategory === category.id;
    
    // For forms, only show childless categories
    if (forForm && hasChildren) {
      return null;
    }
    
    return (
      <Box key={category.id}>
        <MenuItem
          value={category.id}
          onClick={() => onCategorySelect(category.id)}
          bg={isSelected ? "blue.50" : "transparent"}
          color={isSelected ? "blue.600" : "inherit"}
          fontWeight={isSelected ? "600" : "normal"}
          pr={4 + (level * 20)}
          pl={4}
          position="relative"
          _hover={{ bg: "gray.100" }}
          minH="40px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          textAlign="right"
        >
          {/* Visual indicator for hierarchy - RTL positioning */}
          {level > 0 && (
            <Box
              position="absolute"
              right={4 + ((level - 1) * 20)}
              top="50%"
              transform="translateY(-50%)"
              width="12px"
              height="1px"
              bg="gray.300"
            />
          )}
          
          {/* Category name with proper spacing - RTL layout */}
          <Text 
            flex="1" 
            textAlign="right"
            fontSize="sm"
            order={2}
          >
            {category.name}
          </Text>
          
          {/* Arrow indicator for categories with children - RTL positioning */}
          {hasChildren && (
            <Box
              as="span"
              mr={2}
              color="gray.400"
              fontSize="xs"
              order={1}
            >
              ◀
            </Box>
          )}
        </MenuItem>
        
        {/* Render children recursively with increased indentation */}
        {hasChildren && (
          <Box>
            {children.map(child => renderCategoryItem(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const getSelectedCategoryName = () => {
    if (!selectedCategory) return placeholder;
    
    const findCategoryName = (cats, id) => {
      if (!Array.isArray(cats)) return null;
      
      for (const cat of cats) {
        if (cat.id == id) return cat.name;
        
        // Check different possible children properties
        const children = cat.children || cat.subcategories || cat.child_categories;
        if (children && Array.isArray(children)) {
          const found = findCategoryName(children, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findCategoryName(categories, selectedCategory) || placeholder;
  };

  // If no categories, show loading or empty state
  if (!Array.isArray(categories) || categories.length === 0) {
    return (
      <Button
        variant="outline"
        minW="200px"
        textAlign="right"
        justifyContent="space-between"
        bg="white"
        borderColor="gray.300"
        isDisabled
      >
        <Text>جاري التحميل...</Text>
      </Button>
    );
  }

  // Get appropriate categories based on usage
  const categoriesToRender = forForm ? getChildlessCategories(categories) : getRootCategories(categories);

  return (
    <Menu 
      placement="bottom-end" 
      autoSelect={false} 
      closeOnSelect={true}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
    >
      <MenuButton
        as={Button}
        leftIcon={<ChevronDownIcon />}
        variant="outline"
        minW="200px"
        textAlign="right"
        justifyContent="space-between"
        bg="white"
        borderColor="gray.300"
        _hover={{ borderColor: "gray.400" }}
        _expanded={{ borderColor: "blue.500" }}
      >
        <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {getSelectedCategoryName()}
        </Text>
      </MenuButton>
      
      <Portal>
        <MenuList 
          zIndex={zIndex + 1000} 
          maxH="400px" 
          overflowY="auto" 
          minW="280px" 
          textAlign="right"
          position="fixed"
          onWheel={(e) => e.stopPropagation()}
          onScroll={(e) => e.stopPropagation()}
          style={{
            overscrollBehavior: 'contain',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent'
          }}
        >
          {!forForm && (
            <>
              <MenuItem
                onClick={() => onCategorySelect("")}
                bg={!selectedCategory ? "blue.50" : "transparent"}
                color={!selectedCategory ? "blue.600" : "inherit"}
                fontWeight={!selectedCategory ? "600" : "normal"}
                _hover={{ bg: "gray.100" }}
                minH="40px"
                textAlign="right"
              >
                جميع الفئات
              </MenuItem>
              <MenuDivider />
            </>
          )}
          
          {/* Render appropriate categories based on usage */}
          {categoriesToRender.map(category => renderCategoryItem(category, 0))}
        </MenuList>
      </Portal>
    </Menu>
  );
}

// Size Modal Component
function SizeModal({ isOpen, onClose, size, onSubmit, actionLoading, validationErrors }) {
  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    additional_info: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const toast = useToast();

  // Fetch available categories
  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await categoriesService.getCategoriesForFilter();
      console.log('Modal categories response:', response);
      if (response.success) {
        const categoriesData = response.data || [];
        console.log('Modal categories data:', categoriesData);
        console.log('Modal categories data type:', typeof categoriesData);
        console.log('Modal categories is array:', Array.isArray(categoriesData));
        if (Array.isArray(categoriesData)) {
          console.log('Modal categories length:', categoriesData.length);
          console.log('Modal first category:', categoriesData[0]);
        }
        setAvailableCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } else {
        console.log('Modal categories fetch failed:', response.message);
        setAvailableCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setAvailableCategories([]);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الفئات",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (size?.id) {
      // Editing existing size
      setFormData({
        name: size.name || "",
        category_id: size.category_id || "",
        additional_info: size.additional_info || ""
      });
    } else {
      // Creating new size
      setFormData({
        name: "",
        category_id: "",
        additional_info: ""
      });
    }
    
    // Fetch available categories
    if (isOpen) {
      fetchCategories();
    }
  }, [size, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.category_id) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error in size modal submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent mx={4} zIndex={9999}>
        <ModalHeader textAlign="center">
          {size?.id ? "تعديل المقاس" : "إضافة مقاس جديد"}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={!!validationErrors.name}>
                <FormLabel textAlign="right">اسم المقاس</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="أدخل اسم المقاس"
                  textAlign="right"
                />
                <FormErrorMessage>{validationErrors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!validationErrors.category_id}>
                <FormLabel textAlign="right">الفئة</FormLabel>
                <NestedCategorySelector
                  categories={availableCategories}
                  selectedCategory={formData.category_id}
                  onCategorySelect={(value) => handleInputChange('category_id', value)}
                  placeholder="اختر الفئة"
                  zIndex={10000}
                  forForm={true}
                />
                <FormErrorMessage>{validationErrors.category_id}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel textAlign="right">معلومات إضافية</FormLabel>
                <Input
                  value={formData.additional_info}
                  onChange={(e) => handleInputChange('additional_info', e.target.value)}
                  placeholder="معلومات إضافية (اختياري)"
                  textAlign="right"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button variant="ghost" mr={3} onClick={onClose}>
              إلغاء
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
            >
              {size?.id ? "تحديث" : "إضافة"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default function Sizes() {
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "navy.700");
  
  const [sizes, setSizes] = useState([]);
  const [filteredSizes, setFilteredSizes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [deletingSize, setDeletingSize] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [categories, setCategories] = useState([]);
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const toast = useToast();

  useEffect(() => {
    fetchSizes();
    fetchCategories();
    
    // Test API call to see raw response
    const testAPI = async () => {
      try {
        console.log('Testing API directly...');
        const rawResponse = await fetch('/api/admin/sizes');
        const rawData = await rawResponse.json();
        console.log('Raw API response:', rawData);
        console.log('Raw response status:', rawResponse.status);
        console.log('Raw response headers:', rawResponse.headers);
      } catch (error) {
        console.error('Test API call failed:', error);
      }
    };
    
    testAPI();
  }, []);

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (isModalOpen || isDeleteOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'contain';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.overscrollBehavior = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.overscrollBehavior = 'unset';
    };
  }, [isModalOpen, isDeleteOpen]);

  // Prevent table scrolling when category menu is open
  useEffect(() => {
    const tableContainer = document.querySelector('.sizes-table-container');
    if (tableContainer) {
      if (isModalOpen || isDeleteOpen) {
        tableContainer.style.overflow = 'hidden';
        tableContainer.style.overscrollBehavior = 'contain';
      } else {
        tableContainer.style.overflow = 'auto';
        tableContainer.style.overscrollBehavior = 'unset';
      }
    }
  }, [isModalOpen, isDeleteOpen]);

  useEffect(() => {
    // When category filter changes, fetch sizes with new filter
    // Only fetch if this is not the initial load (categories have been fetched)
    if (categories.length > 0) {
      fetchSizes(true); // true indicates this is a filter change
    }
  }, [selectedCategory]);

  useEffect(() => {
    // Apply search filter locally since it's faster for typing
    applySearchFilter();
  }, [searchTerm, sizes]);

  const fetchSizes = async (isFilterChange = false) => {
    if (isFilterChange) {
      setIsFilterLoading(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const filters = {};
      if (selectedCategory) filters.category_id = selectedCategory;
      if (searchTerm) filters.search = searchTerm;
      
      console.log('Fetching sizes with filters:', filters);
      
      const response = await sizesService.getSizes(filters);
      console.log('Sizes response:', response);
      console.log('Response structure:', {
        success: response.success,
        hasData: !!response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
        fullResponse: response
      });
      
      if (response.success) {
        // Try different data access patterns to handle various API response structures
        let sizesData = null;
        
        if (response.data && Array.isArray(response.data)) {
          // Direct array: { success: true, data: [...] }
          sizesData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Nested structure: { success: true, data: { data: [...], status: 1 } }
          sizesData = response.data.data;
        } else if (response.data && response.data.status === 1 && response.data.data && Array.isArray(response.data.data)) {
          // API structure: { success: true, data: { data: [...], status: 1, message: "..." } }
          sizesData = response.data.data;
        }
        
        console.log('Extracted sizes data:', sizesData);
        
        if (sizesData && Array.isArray(sizesData)) {
          setSizes(sizesData);
          setFilteredSizes(sizesData);
        } else {
          console.error('No valid sizes data found in response');
          setSizes([]);
          setFilteredSizes([]);
        }
      } else {
        console.log('Sizes fetch failed:', response.message);
        
        // Fallback: try to extract data even if success is false
        if (response.data && Array.isArray(response.data)) {
          console.log('Fallback: Found data in response.data despite success: false');
          setSizes(response.data);
          setFilteredSizes(response.data);
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          console.log('Fallback: Found data in response.data.data despite success: false');
          setSizes(response.data.data);
          setFilteredSizes(response.data.data);
        } else {
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء جلب المقاسات",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching sizes:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب المقاسات",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      if (isFilterChange) {
        setIsFilterLoading(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesService.getCategoriesForFilter();
      console.log('Categories response:', response);
      if (response.success) {
        const categoriesData = response.data || [];
        console.log('Categories data:', categoriesData);
        console.log('Categories data type:', typeof categoriesData);
        console.log('Categories is array:', Array.isArray(categoriesData));
        if (Array.isArray(categoriesData)) {
          console.log('Categories length:', categoriesData.length);
          console.log('First category:', categoriesData[0]);
          
          // Check for hierarchical structure
          const hasHierarchicalStructure = categoriesData.some(cat => 
            (cat.children && Array.isArray(cat.children)) ||
            (cat.subcategories && Array.isArray(cat.subcategories)) ||
            (cat.child_categories && Array.isArray(cat.child_categories))
          );
          console.log('Has hierarchical structure:', hasHierarchicalStructure);
          
          if (hasHierarchicalStructure) {
            console.log('Hierarchical categories found!');
            // Log the structure of first few categories
            categoriesData.slice(0, 3).forEach((cat, index) => {
              console.log(`Category ${index + 1}:`, {
                id: cat.id,
                name: cat.name,
                children: cat.children,
                subcategories: cat.subcategories,
                child_categories: cat.child_categories
              });
            });
          }
        }
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } else {
        console.log('Categories fetch failed:', response.message);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const applySearchFilter = () => {
    if (!Array.isArray(sizes)) {
      setFilteredSizes([]);
      return;
    }

    // Only apply search filter locally since category filter is handled by API
    let filtered = sizes;

    if (searchTerm) {
      filtered = filtered.filter(size => 
        size.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        size.additional_info?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSizes(filtered);
  };

  const handleCreateSize = async (sizeData) => {
    try {
      console.log('Creating size with data:', sizeData);
      const response = await sizesService.createSize(sizeData);
      console.log('Create size response:', response);
      
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم إنشاء المقاس بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchSizes();
      } else {
        console.log('Create size failed:', response);
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء إنشاء المقاس",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error creating size:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء إنشاء المقاس",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateSize = async (sizeData) => {
    try {
      console.log('Updating size with data:', sizeData);
      const response = await sizesService.updateSize(editingSize.id, sizeData);
      console.log('Update size response:', response);
      
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم تحديث المقاس بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchSizes();
        setEditingSize(null);
      } else {
        console.log('Update size failed:', response);
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء تحديث المقاس",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating size:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث المقاس",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteSize = async () => {
    if (!deletingSize) return;

    try {
      const response = await sizesService.deleteSize(deletingSize.id);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم حذف المقاس بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchSizes();
        setDeletingSize(null);
        onDeleteClose(); // Close the delete dialog after successful deletion
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء حذف المقاس",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting size:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف المقاس",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openEditModal = (size) => {
    setEditingSize(size);
    onModalOpen();
  };

  const openDeleteDialog = (size) => {
    setDeletingSize(size);
    onDeleteOpen();
  };

  const closeModal = () => {
    setEditingSize(null);
    setValidationErrors({});
    onModalClose();
  };

  const handleSubmit = async (formData) => {
    if (editingSize) {
      await handleUpdateSize(formData);
    } else {
      await handleCreateSize(formData);
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        <Flex direction="column" w="100%">
          <Flex
            direction={{ sm: "column", lg: "row" }}
            justify={{ sm: "center", lg: "space-between" }}
            align={{ sm: "center", lg: "center" }}
            minH="70px"
            gap={{ sm: "20px", lg: "0px" }}
            mb="20px"
          >
            <Text
              color={textColor}
              fontSize={{ base: "lg", sm: "xl" }}
              fontWeight="700"
              lineHeight="100%"
            >
              إدارة المقاسات
            </Text>
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              onClick={onModalOpen}
            >
              إضافة مقاس جديد
            </Button>
          </Flex>

          {/* Filters */}
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={4}
            mb={6}
            align={{ base: "stretch", md: "center" }}
          >
            <Box flex={1}>
              <InputGroup>
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                                 <Input
                   placeholder="البحث في المقاسات..."
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   textAlign="right"
                 />
              </InputGroup>
            </Box>
                         <Box minW="200px">
               <NestedCategorySelector
                 categories={categories}
                 selectedCategory={selectedCategory}
                 onCategorySelect={setSelectedCategory}
                 placeholder="جميع الفئات"
                 zIndex={9999}
                 forForm={false}
               />
               {isFilterLoading && (
                 <Text fontSize="xs" color="blue.500" mt={1} textAlign="right">
                   جاري تحديث المقاسات...
                 </Text>
               )}
             </Box>
          </Flex>

                    {/* Table */}
          <Box 
            className="sizes-table-container"
            overflowX="auto" 
            position="relative" 
            zIndex={1}
            style={{ 
              pointerEvents: (isModalOpen || isDeleteOpen) ? 'none' : 'auto',
              userSelect: (isModalOpen || isDeleteOpen) ? 'none' : 'auto',
              overscrollBehavior: 'contain'
            }}
          >
            <TableContainer>
              <Table variant="simple">
                                 <Thead>
                   <Tr>
                     <Th>المقاس</Th>
                     <Th>الفئة</Th>
                     <Th>معلومات إضافية</Th>
                     <Th>الإجراءات</Th>
                   </Tr>
                 </Thead>
                <Tbody>
                                     {isLoading || isFilterLoading ? (
                     <Tr>
                       <Td colSpan={4} textAlign="center">
                         <Flex justify="center" align="center" p={4}>
                           <Spinner size="md" color="blue.500" />
                           <Text ml={3}>جاري التحميل...</Text>
                         </Flex>
                       </Td>
                     </Tr>
                   ) : (!Array.isArray(filteredSizes) || filteredSizes.length === 0) ? (
                     <Tr>
                       <Td colSpan={4} textAlign="center">
                         <Text color="gray.500">لا توجد مقاسات</Text>
                       </Td>
                     </Tr>
                   ) : (
                     Array.isArray(filteredSizes) && filteredSizes.map((size) => (
                       <Tr key={size.id}>
                         <Td>
                           <Text fontWeight="600">{size.name}</Text>
                         </Td>
                         <Td>
                           <Badge colorScheme="blue" variant="subtle">
                             {size.category?.name || 'غير محدد'}
                           </Badge>
                         </Td>
                         <Td>
                           <Text color="gray.600" fontSize="sm">
                             {size.additional_info || '-'}
                           </Text>
                         </Td>
                         <Td>
                           <HStack spacing={2}>
                             <Tooltip label="تعديل">
                               <IconButton
                                 size="sm"
                                 icon={<EditIcon />}
                                 onClick={() => openEditModal(size)}
                                 colorScheme="blue"
                                 variant="ghost"
                               />
                             </Tooltip>
                             <Tooltip label="حذف">
                               <IconButton
                                 size="sm"
                                 icon={<DeleteIcon />}
                                 onClick={() => openDeleteDialog(size)}
                                 colorScheme="red"
                                 variant="ghost"
                               />
                             </Tooltip>
                           </HStack>
                         </Td>
                       </Tr>
                     ))
                   )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Flex>
      </Card>

      {/* Size Modal */}
      <SizeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        size={editingSize}
        onSubmit={handleSubmit}
        actionLoading={actionLoading}
        validationErrors={validationErrors}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        leastDestructiveRef={undefined}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <AlertDialogContent 
          mx={4} 
          zIndex={9999}
          bg="white"
          borderRadius="xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="gray.200"
          maxW="500px"
          transform="scale(1)"
          transition="all 0.2s ease-in-out"
          _hover={{ transform: "scale(1.02)" }}
        >
          <AlertDialogHeader 
            fontSize="xl" 
            fontWeight="bold"
            textAlign="center"
            color="red.600"
            borderBottom="1px solid"
            borderColor="gray.200"
            pb={4}
          >
            ⚠️ حذف المقاس
          </AlertDialogHeader>
          <AlertDialogBody py={6} textAlign="center">
            <Text fontSize="md" color="gray.700" mb={4}>
              هل أنت متأكد من حذف المقاس
            </Text>
            <Text 
              fontSize="lg" 
              fontWeight="bold" 
              color="red.600" 
              bg="red.50" 
              p={3} 
              borderRadius="md"
              border="1px solid"
              borderColor="red.200"
              mb={4}
            >
              "{deletingSize?.name}"
            </Text>
            <Text fontSize="sm" color="gray.600">
              لا يمكن التراجع عن هذا الإجراء.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter 
            justifyContent="center" 
            gap={3}
            borderTop="1px solid"
            borderColor="gray.200"
            pt={4}
          >
            <Button 
              onClick={onDeleteClose}
              variant="outline"
              size="lg"
              px={8}
              _hover={{ bg: "gray.50" }}
            >
              إلغاء
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDeleteSize}
              size="lg"
              px={8}
              _hover={{ bg: "red.600" }}
              leftIcon={<DeleteIcon />}
            >
              حذف
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
}
